import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";

const API_BASE_URL = "https://bluewaterdubai.websiteseoonline.com";
const OFFPLAN_LIMIT = 8;
const OFFPLAN_ENDPOINTS = ["/api/offplanprojectHomePage.ashx", "/api/offplanprojectbydeveloper.ashx"];
const FALLBACK_IMAGE = "assets/img/gallery/gallery-1-1.jpg";

function buildApiUrl(pathname) {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || API_BASE_URL).replace(/\/$/, "");
  return `${baseUrl}${pathname}`;
}

async function fetchOffPlanProjects(signal) {
  const body = JSON.stringify({ id: "", page: 1, pageSize: OFFPLAN_LIMIT });
  let lastError = null;

  for (const endpoint of OFFPLAN_ENDPOINTS) {
    try {
      const response = await fetch(buildApiUrl(endpoint), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body,
        signal,
      });

      if (!response.ok) {
        throw new Error(`Unable to load off-plan projects from ${endpoint}`);
      }

      const payload = await response.json();

      if (payload.result === 1 && Array.isArray(payload.data)) {
        return payload.data.slice(0, OFFPLAN_LIMIT);
      }

      throw new Error(payload.message || `Unable to load off-plan projects from ${endpoint}`);
    } catch (err) {
      if (err.name === "AbortError") {
        throw err;
      }

      lastError = err;
    }
  }

  throw lastError || new Error("Unable to load off-plan projects");
}

function offPlanUrl(project) {
  const referenceNumber = project.reference_number || project.referenceNumber || project.property_id || project.id;
  const slug = String(project.url || project.title || "")
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean)[0];
  const encodedReference = referenceNumber ? encodeURIComponent(referenceNumber) : "";

  if (slug && encodedReference) {
    return "/offplan-details/" + slug + "/" + encodedReference;
  }

  return encodedReference ? "/offplan-details/" + encodedReference : "/offplan-details";
}
function projectDeveloper(project) {
  return project.developername || project.Name || project.developer || project.city || "Off-Plan";
}

function imageWithVariant(image, variant, fallback) {
  return image ? `${image}/${variant}` : fallback;
}

function OffPlanProjects() {
  const [projects, setProjects] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();

    async function loadProjects() {
      setStatus("loading");

      try {
        const data = await fetchOffPlanProjects(controller.signal);
        setProjects(data);
        setStatus("ready");
      } catch (err) {
        if (err.name !== "AbortError") {
          setProjects([]);
          setStatus("error");
        }
      }
    }

    loadProjects();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (status !== "ready" || !projects.length) {
      return undefined;
    }

    let swiper = null;
    let retryTimer = null;
    let cancelled = false;

    function initSlider() {
      const slider = document.getElementById("offPlanGallerySlider");

      if (cancelled || !slider) {
        return;
      }

      if (!window.Swiper) {
        retryTimer = window.setTimeout(initSlider, 200);
        return;
      }

      if (slider.swiper) {
        slider.swiper.update();
        return;
      }

      swiper = new window.Swiper(slider, {
        autoplay: true,
        centeredSlides: true,
        loop: true,
        spaceBetween: 24,
        navigation: {
          nextEl: slider.querySelector(".slider-next"),
          prevEl: slider.querySelector(".slider-prev"),
        },
        pagination: {
          el: slider.querySelector(".slider-pagination2"),
          clickable: true,
        },
        breakpoints: {
          0: { slidesPerView: 1, spaceBetween: 20 },
          576: { slidesPerView: 2 },
          768: { slidesPerView: 2 },
          992: { slidesPerView: 2 },
          1200: { slidesPerView: 3.4 },
          1400: { slidesPerView: 4.5 },
          1500: { slidesPerView: 4.5 },
        },
      });
    }

    initSlider();

    return () => {
      cancelled = true;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
      if (swiper) {
        swiper.destroy(true, true);
      }
    };
  }, [projects, status]);

  return (
    <div className="gallery-sec-1 overflow-hidden space overflow-hidden">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-7">
            <div className="title-area text-center">
              <h2 className="sec-title mb-2 fadeinup wow" data-wow-duration="1.5s" data-wow-delay="0.3s">
                Find Off-Plan Properties
              </h2>
            </div>
          </div>
        </div>
      </div>
      {status === "loading" && (
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 text-center">Loading off-plan properties...</div>
          </div>
        </div>
      )}
      {status === "error" && (
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 text-center">Unable to load off-plan properties.</div>
          </div>
        </div>
      )}
      {status === "ready" && projects.length > 0 && (
        <div className="container-fluid p-0">
          <div className="slider-area">
            <div className="swiper react-managed-slider has-shadow gallery-1" id="offPlanGallerySlider">
              <div className="swiper-wrapper">
                {projects.map((project) => (
                  <div className="swiper-slide" key={project.id || project.title}>
                    <div className="gallery-card">
                      <div className="gallery-img img-shine">
                        <img src={imageWithVariant(project.ImagePath, "w=340,sharpen=1", FALLBACK_IMAGE)} alt={project.title || "Off-plan property"} />
                        <div className="gallery-content">
                          <div className="gallery-content-wrapper">
                            <p className="box-text">{projectDeveloper(project)}</p>
                            <h2 className="box-title">
                              <Link to={offPlanUrl(project)}>{project.title || "Off-Plan Property"}</Link>
                            </h2>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button className="slider-arrow slider-prev style2" type="button">
                <i className="far fa-arrow-left" />
              </button>
              <button className="slider-arrow slider-next style2" type="button">
                <i className="far fa-arrow-right" />
              </button>
              <div className="slider-pagination-wrapper gallery-1-pagi">
                <div className="slider-pagination2" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OffPlanProjects;
